import React, { useRef, useEffect, useCallback, useMemo, useState, memo } from 'react'
import { Input, Addon, Icon, InputProps, alertsStore, ButtonLink } from '../../ui-kit'
import { canUseDOM } from '../../utils/hoc'
import { i18n } from '../../utils/intl'
import { log } from '../../utils/log'

const HAS_GEOLOCATION = canUseDOM && navigator && navigator.geolocation

interface ReactGoogleAutocompleteProps {
  onPlaceSelected?: (place?: google.maps.places.PlaceResult | google.maps.GeocoderResult) => void

  onCurrentPosition?: (place?: google.maps.GeocoderResult) => void

  types?: string[]

  componentRestrictions?: google.maps.places.ComponentRestrictions

  bounds?: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral

  size?: InputProps['inputSize']

  initialPlaceId?: string

  initialPlaceName?: string

  placeId?: string

  hasLeftAddons?: boolean
}

/**
 * Input with place autocomplete from Google Maps Places API
 */
export const PlacesAutocompleteInput = memo(function PlacesAutocompleteInput(
  props: ReactGoogleAutocompleteProps
) {
  const {
    types = [],
    componentRestrictions = { country: 'sk' },
    onPlaceSelected = () => ({}),
    onCurrentPosition = () => ({}),
    bounds,
    size,
    initialPlaceId,
    initialPlaceName,
    hasLeftAddons = true,
  } = props

  const config: google.maps.places.AutocompleteOptions = useMemo(() => {
    return {
      componentRestrictions,
      types,
      bounds,
    }
  }, [componentRestrictions, types, bounds])

  const inputRef = useRef<HTMLInputElement>(null)
  const autocomplete = useRef<google.maps.places.Autocomplete | null>(null)
  const [isDisabled, setIsDisabled] = useState(false)

  const handlePlaceChanged = useCallback(
    (place: google.maps.places.PlaceResult | google.maps.GeocoderResult) => {
      onPlaceSelected(place)
    },
    [onPlaceSelected]
  )

  useEffect(() => {
    autocomplete.current = new google.maps.places.Autocomplete(inputRef.current!, config)

    const event = autocomplete.current.addListener('place_changed', () => {
      const place = autocomplete.current!.getPlace()
      if (place.address_components == null) {
        alertsStore.create({
          type: 'error',
          content: i18n.t`Vyberte položku zo zoznamu`,
        })
        return
      }
      handlePlaceChanged(place)
    })

    if (initialPlaceId) {
      const placeRequest: google.maps.places.PlaceDetailsRequest = {
        placeId: initialPlaceId,
        fields: ['name', 'place_id', 'geometry', 'formatted_address'],
      }

      const placeService = new google.maps.places.PlacesService(inputRef.current!)

      placeService.getDetails(placeRequest, placeResult => {
        // TODO: check for undefined
        inputRef.current!.value = placeResult!.formatted_address || ''
        handlePlaceChanged(placeResult!)
      })
    }

    return () => {
      event.remove()
    }
  }, [handlePlaceChanged, config, initialPlaceId])

  return (
    <Input
      inputRef={inputRef}
      formControlClass="mb"
      isDisabled={isDisabled}
      inputSize={size}
      placeholder={i18n.t`Hľadať ulicu, mesto, miesto`}
      onChange={e => {
        if (e.currentTarget.value === '') {
          onPlaceSelected()
        }
      }}
      defaultValue={initialPlaceName}
      style={{ paddingLeft: hasLeftAddons ? '0.5rem' : '0.9rem' }}
      rightAddons={
        HAS_GEOLOCATION ? (
          <Addon
            type="action"
            onClick={_ => {
              setIsDisabled(true)
              navigator.geolocation.getCurrentPosition(
                pos => {
                  const { latitude, longitude } = pos.coords
                  inputRef.current!.placeholder = i18n.t`Blízko mojej polohy`
                  inputRef.current!.value = ''
                  setIsDisabled(false)

                  const geocoder = new google.maps.Geocoder()

                  const latlng = { lat: latitude, lng: longitude }

                  geocoder.geocode({ location: latlng }, (results, status) => {
                    if (status === google.maps.GeocoderStatus.OK) {
                      if (results![0]) {
                        onCurrentPosition(results![0])
                        handlePlaceChanged(results![0])
                      } else {
                        log('PlacesAutocompleteInput.geocoder.geocode: place noot found')
                      }
                      /* eslint-enable */
                    } else {
                      log('PlacesAutocompleteInput.geocoder.geocode: error', status)
                    }
                  })
                },
                () => {
                  alertsStore.create({
                    type: 'error',
                    content: i18n.t`Nepodarilo sa získať vašu aktuálnu polohu.`,
                  })
                  setIsDisabled(false)
                }
              )
            }}>
            <ButtonLink equal isDisabled={isDisabled}>
              <Icon size="medium" name="locate" />
            </ButtonLink>
          </Addon>
        ) : null
      }
      leftAddons={
        hasLeftAddons && (
          <Addon type="filled">
            <Icon size="medium" name="search-glass" />
          </Addon>
        )
      }
    />
  )
})
